/**
 * SABA account helpers (#808)
 *
 * 1. Signed-in success toast -- Shopify's native sign-in strips any query we put on
 *    `return_to` (it only keeps the path and appends its own `shop_sign_in`), so we can't
 *    rely on a URL flag surviving the round-trip. Instead we stamp a flag in localStorage
 *    when a sign-in CTA is clicked, then on the returned page show the toast if that flag
 *    is set AND the customer is now signed in, then clear it. Works for every sign-in
 *    method (email / Shop / social).
 * 2. Acquisition source relay -- the CTA's data-saba-source is stamped alongside, and
 *    when the customer returns signed in an "Account Sign Up" / "Account Sign In"
 *    event with that source is sent straight to Klaviyo's Client Events API
 *    (fetch keepalive), independent of klaviyo.js load state. Customer data comes
 *    from the layout-rendered #saba-klaviyo-customer carrier; the public key comes
 *    from the theme setting, falling back to the app embed's script tag URL.
 *    Sign-up vs sign-in (Shopify's merged auth hides which happened, and Liquid
 *    has no customer.created_at): an email this browser already relayed is a
 *    sign-in; otherwise the Omneo CX profile's join time decides (enrolment is
 *    automatic on signup, so it mirrors account creation); zero-orders heuristic
 *    only if that lookup fails.
 *    The same events also signal GA4 (#957) via the GTM dataLayer already
 *    bootstrapped in theme.liquid: createAccount / loginAccount with em
 *    (SHA-256 of the lowercased email) + plain email.
 */
(function () {
  'use strict';

  var PENDING_KEY = 'saba_signin_pending';
  var SOURCE_KEY = 'saba_signin_source';
  var RELAYED_KEY = 'saba_relay_seen'; // emails this browser has already relayed a metric for
  var GA4_KEY = 'saba_ga4_signalled'; // flag timestamp already pushed to the dataLayer (GA4 fires once per flag; Klaviyo retries independently)
  var PENDING_MAX_AGE = 15 * 60 * 1000; // 15 minutes -- sign-in toast
  var SOURCE_MAX_AGE = 60 * 60 * 1000; // 60 minutes -- a first-time signup (email code, consents) can far outlive 15

  // The header renders `.saba-account__greeting` only for a signed-in customer.
  function isSignedIn() {
    return !!document.querySelector('.saba-account__greeting');
  }

  function clearPending() {
    try { localStorage.removeItem(PENDING_KEY); } catch (err) {}
  }

  function sourceIsFresh() {
    try {
      var data = JSON.parse(localStorage.getItem(SOURCE_KEY));
      return !!(data && data.ts && Date.now() - data.ts <= SOURCE_MAX_AGE);
    } catch (err) {
      return false;
    }
  }

  function pendingIsFresh() {
    try {
      var ts = parseInt(localStorage.getItem(PENDING_KEY), 10);
      if (!ts) return false;
      if (Date.now() - ts > PENDING_MAX_AGE) { clearPending(); return false; }
      return true;
    } catch (err) {
      return false;
    }
  }

  // Stamp intent when any native sign-in CTA is clicked, so we can confirm on return.
  // Matches our own popup attribute first (endpoint-agnostic, any element -- the footer
  // row is a div), then the current Shopify auth path for plain sign-in links, then the
  // header <shopify-account> trigger itself (its slot is a span, not a link).
  // Alongside the intent flag we record the CTA's acquisition source (data-saba-source
  // on the element or an ancestor, falling back to the page path) for the Klaviyo relay.
  function initSigninIntent() {
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      var link = event.target.closest('[data-saba-signin-popup], a[href*="/customer_authentication/login"], shopify-account');
      if (!link) return;
      try { localStorage.setItem(PENDING_KEY, String(Date.now())); } catch (err) {}

      // Clicks inside the account sheet retarget (shadow DOM) to the <shopify-account>
      // host, which carries data-saba-source="header" -- so a sheet opened from another
      // CTA (footer, signup button) would get its source overwritten to "header" on
      // every click inside it. For the component itself, keep an existing fresh source;
      // explicit CTAs always overwrite.
      var isComponent = link.tagName && link.tagName.toLowerCase() === 'shopify-account';
      if (isComponent && sourceIsFresh()) return;

      var sourceEl = link.closest('[data-saba-source]');
      var source = sourceEl ? sourceEl.getAttribute('data-saba-source') : '';
      try {
        localStorage.setItem(SOURCE_KEY, JSON.stringify({
          source: source || '',
          page: window.location.pathname,
          ts: Date.now()
        }));
      } catch (err) {}
    });
  }

  // Sign-up buttons in "popup" mode (blocks/signup-button): open the header's
  // <shopify-account> on-page sheet. The component exposes showModal()/close()
  // (present on the live component, not yet documented) -- prefer that; fall back
  // to clicking its slotted trigger; fall through to the link's hosted sign-in
  // href when the component isn't available at all.
  function initSigninPopupButtons() {
    document.addEventListener('click', function (event) {
      var wrapper = event.target.closest && event.target.closest('[data-saba-signin-popup]');
      if (!wrapper) return;
      var component = document.querySelector('shopify-account');
      if (!component) return; // no component -> let the link navigate

      if (typeof component.showModal === 'function') {
        event.preventDefault();
        component.showModal();
        return;
      }

      var trigger = component.querySelector('.saba-account__login');
      if (!trigger) return;
      var rect = trigger.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return; // hidden -> navigate
      event.preventDefault();
      trigger.click();
    });
  }

  function initToast() {
    // Note: we deliberately do NOT strip Shopify's own post-sign-in params
    // (shop_sign_in / shop_consented_scopes) - they're Shopify's to manage and its
    // scripts may read them on load. The toast doesn't depend on any URL param.
    var host = document.querySelector('saba-signup-success-toast');
    if (!host) return;

    // Only confirm when the customer just came back from a sign-in CTA and is now in.
    if (!pendingIsFresh() || !isSignedIn()) return;
    clearPending();

    host.hidden = false;
    window.requestAnimationFrame(function () {
      host.classList.add('is-visible');
    });

    var hideTimer;
    var dismiss = function () {
      window.clearTimeout(hideTimer);
      host.classList.remove('is-visible');
      window.setTimeout(function () {
        host.hidden = true;
      }, 300);
    };

    hideTimer = window.setTimeout(dismiss, 4000);

    var closeBtn = host.querySelector('[data-toast-close]');
    if (closeBtn) closeBtn.addEventListener('click', dismiss);
  }

  // Public key: theme setting first (carrier attribute), else parsed from the
  // Klaviyo app embed's own script tag URL.
  function klaviyoCompanyId(carrier) {
    var fromSetting = carrier.getAttribute('data-klaviyo-company');
    if (fromSetting) return fromSetting;
    var script = document.querySelector('script[src*="static.klaviyo.com/onsite/js"]');
    var match = script && script.src.match(/[?&]company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : '';
  }

  function alreadyRelayed(email) {
    try { return (JSON.parse(localStorage.getItem(RELAYED_KEY)) || []).indexOf(email) !== -1; } catch (err) { return false; }
  }

  // First-seen email: ask the Omneo CX proxy for the member's join time (enrolment
  // is automatic on signup, so it mirrors Shopify account creation). No profile yet,
  // or joined at/after the CTA click, means this flow created the account. Falls
  // back to the zero-orders heuristic when the proxy is unreachable. Omneo
  // timestamps are UTC with no zone suffix.
  function resolveMetric(carrier, clickTs, done) {
    window.fetch('/apps/cx')
      .then(function (response) {
        if (!response.ok) throw new Error('cx ' + response.status);
        return response.json();
      })
      .then(function (body) {
        var joined = body && body.profile && (body.profile.joined_at || body.profile.created_at);
        if (!joined) { done('Account Sign Up'); return; }
        var joinedMs = Date.parse(joined.replace(' ', 'T') + 'Z');
        if (isNaN(joinedMs)) throw new Error('unparseable join time');
        done(joinedMs >= clickTs - 60000 ? 'Account Sign Up' : 'Account Sign In');
      })
      .catch(function () {
        done(carrier.getAttribute('data-customer-orders') === '0' ? 'Account Sign Up' : 'Account Sign In');
      });
  }

  function markRelayed(email) {
    try {
      var seen = [];
      try { seen = JSON.parse(localStorage.getItem(RELAYED_KEY)) || []; } catch (err) {}
      if (seen.indexOf(email) === -1) {
        seen.push(email);
        if (seen.length > 10) seen = seen.slice(-10);
        localStorage.setItem(RELAYED_KEY, JSON.stringify(seen));
      }
    } catch (err) {}
  }

  // Relay the acquisition source to Klaviyo once the customer returns signed in.
  // Independent of the toast's PENDING_KEY so neither consumes the other's flag.
  function initSourceRelay() {
    var carrier = document.getElementById('saba-klaviyo-customer');
    if (!carrier) return;
    var email = carrier.getAttribute('data-customer-email');
    var companyId = klaviyoCompanyId(carrier);
    if (!email || !companyId) return;

    var raw = null;
    try { raw = localStorage.getItem(SOURCE_KEY); } catch (err) { return; }
    if (!raw) return;

    var data = null;
    try { data = JSON.parse(raw); } catch (err) {}
    if (!data || !data.ts || Date.now() - data.ts > SOURCE_MAX_AGE) {
      try { localStorage.removeItem(SOURCE_KEY); } catch (err) {}
      return;
    }

    var props = {
      Source: data.source || data.page || '',
      Page: data.page || ''
    };

    // GA4 signal (#957): push the matching event to the GTM dataLayer with the
    // Meta/GA identity convention -- em = SHA-256 hex of the lowercased email.
    var pushToDataLayer = function (metric) {
      var gaEvent = metric === 'Account Sign Up' ? 'createAccount' : 'loginAccount';
      var push = function (em) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: gaEvent, em: em, email: email });
      };
      try {
        window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()))
          .then(function (buffer) {
            var hex = Array.prototype.map.call(new Uint8Array(buffer), function (b) {
              return ('0' + b.toString(16)).slice(-2);
            }).join('');
            push(hex);
          })
          .catch(function () { push(''); });
      } catch (err) { push(''); } // no SubtleCrypto: still signal the event, just unhashed
    };

    var sendEvent = function (metric) {
      // GA4 once per flag instance: a Klaviyo retry (flag kept on failure) must
      // not push another conversion event into the dataLayer.
      var signalled = null;
      try { signalled = localStorage.getItem(GA4_KEY); } catch (err) {}
      if (String(data.ts) !== signalled) {
        pushToDataLayer(metric);
        try { localStorage.setItem(GA4_KEY, String(data.ts)); } catch (err) {}
      }

      var request;
      try {
        request = window.fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: { data: { type: 'metric', attributes: { name: metric } } },
                properties: props,
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email, properties: { 'Latest Signup Source': props.Source } }
                  }
                }
              }
            }
          })
        });
      } catch (err) { return; } // fetch unavailable: keep the flag for the next load

      // Clear only once Klaviyo accepts: a rejected request (ad blockers commonly
      // target a.klaviyo.com, or a flaky connection) keeps the flag so the next
      // page load retries within the TTL, instead of silently losing the source.
      request
        .then(function (response) {
          if (!response.ok) return;
          markRelayed(email);
          try { localStorage.removeItem(SOURCE_KEY); } catch (err) {}
        })
        .catch(function () {});
    };

    // Same-browser repeat: a previous relay for this email means the account
    // pre-existed, so it's a sign-in -- no lookup needed.
    if (alreadyRelayed(email)) {
      sendEvent('Account Sign In');
      return;
    }
    resolveMetric(carrier, data.ts, sendEvent);
  }

  function init() {
    initSigninIntent();
    initSigninPopupButtons();
    initSourceRelay();
    initToast();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
